import { useCallback, useRef } from 'react';
import { useDemoTour as useDemoTourStore } from '../store/demoTour';
import { DEMO_STEPS, CITY_CENTER, buildTrafficStep } from '../data/demoSteps';
import { detectCongestion } from '../utils/detectCongestion';

const CAMERA_URL = import.meta.env.DEV
  ? 'http://localhost:8310/camera/command'
  : '/camera/command';

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useDemoTourRunner(addAlarm: (driverId: number, code: string) => void) {
  const { touring, startTour, endTour, setHighlights, startAlarmLoop, setTrafficVisible } = useDemoTourStore();
  const cancelRef = useRef(false);
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const run = useCallback(async () => {
    cancelRef.current = false;
    startTour();

    // Build steps with dynamic traffic step injected as step 2
    let steps = [...DEMO_STEPS];
    const apiKey = import.meta.env.VITE_TOMTOM_API_KEY as string | undefined;
    if (apiKey) {
      const congestion = await detectCongestion(apiKey);
      let narrative: string;
      if (congestion) {
        narrative = `Capa de *flujo vehicular en tiempo real* — se detecta !tráfico denso! en ${congestion.streetName}. El sistema correlaciona estado semafórico con flujo vehicular.`;
      } else {
        narrative = 'Capa de *flujo vehicular en tiempo real* — circulación fluida en la red. El sistema correlaciona estado semafórico con flujo vehicular.';
      }
      const center = congestion?.center ?? CITY_CENTER;
      const trafficStep = buildTrafficStep(center, narrative);
      steps = [steps[0], trafficStep, ...steps.slice(1)];
    }

    for (const step of steps) {
      if (cancelRef.current) break;

      // Traffic toggle: hide layer before flying, reveal after arrival
      if (step.toggleTraffic) {
        setTrafficVisible(false);
      }

      // Send camera command
      try {
        await fetch(CAMERA_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'fly_to',
            center: step.center,
            zoom: step.zoom,
            pitch: step.pitch,
            bearing: step.bearing,
            duration: step.duration,
            narrative: step.narrative,
            narrative_duration: step.narrativeDuration,
            alarm: step.alarm ?? null,
          }),
        });
      } catch {
        // Camera server unavailable — continue tour anyway
      }

      setHighlights(step.highlights);

      // Traffic toggle: reveal layer after fly animation completes
      if (step.toggleTraffic) {
        if (cancelRef.current) break;
        await new Promise<void>(resolve => {
          sleepTimerRef.current = setTimeout(resolve, step.duration + 500);
        });
        setTrafficVisible(true);
      }

      // Inject alarm if step has one
      if (step.alarm) {
        addAlarm(step.alarm.driverId, step.alarm.code);
      }

      // Wait for dwell time (cancellable)
      if (cancelRef.current) break;
      await new Promise<void>(resolve => {
        sleepTimerRef.current = setTimeout(resolve, step.toggleTraffic ? step.dwell - step.duration - 500 : step.dwell);
      });
    }

    if (!cancelRef.current) {
      // Tour completed — enter alarm loop mode
      setHighlights([]);
      startAlarmLoop();
    }
    endTour();
  }, [startTour, endTour, setHighlights, startAlarmLoop, addAlarm]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    setHighlights([]);
    setTrafficVisible(true);
    endTour();
    useDemoTourStore.getState().stopAlarmLoop();
  }, [setHighlights, setTrafficVisible, endTour]);

  return { touring, run, cancel };
}
